import React, { useEffect } from 'react';
import Header from '../layout/Header';
import Footer from '../layout/Footer';

const EFFECTIVE_DATE = '1 September 2026';

const PrivacyPolicyPage = () => {
  useEffect(() => {
    document.title = 'Privacy Policy · AutoPostr';
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Header />
      <main className="pt-32 pb-24 px-6">
        <article className="max-w-3xl mx-auto" data-testid="privacy-policy-content">
          <header className="mb-10">
            <p className="text-sm text-white/50 uppercase tracking-wider mb-3">Legal</p>
            <h1 className="text-3xl md:text-5xl font-bold mb-3">Privacy Policy</h1>
            <p className="text-white/60 text-sm">Effective date: {EFFECTIVE_DATE}</p>
          </header>

          <section className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed mb-8">
              AutoPostr (&quot;AutoPostr&quot;, &quot;we&quot;, &quot;us&quot; or &quot;our&quot;) provides a
              SaaS social-media management and content publishing platform that allows users
              to connect supported social-media accounts, create and upload content, schedule
              posts, manage connected accounts and publish content to supported social-media
              platforms. This Privacy Policy explains what information AutoPostr collects, how
              we use it, and the choices available to users.
            </p>

            <H2>1. Information We Collect</H2>
            <P>AutoPostr may collect the following categories of information:</P>
            <UL items={[
              'Name',
              'Email address',
              'Account and login information',
              'Content uploaded or created by users',
              'Images, videos and other media',
              'Social-media account identifiers',
              'Public profile information',
              'Information received through Facebook, Instagram and Threads APIs',
              'Access tokens and authentication information required to connect social accounts',
              'Scheduled posts and publishing information',
              'Usage, device and technical information',
              'Information necessary to provide customer support and maintain the service',
            ]} />

            <H2>2. How We Use Information</H2>
            <P>AutoPostr uses information to:</P>
            <UL items={[
              'Provide the AutoPostr service',
              'Create and authenticate user accounts',
              'Connect users\u2019 social-media accounts',
              'Allow users to create, upload and manage content',
              'Allow users to schedule and publish content',
              'Process scheduled posts',
              'Manage connected social-media accounts',
              'Provide analytics and other supported platform features',
              'Monitor and maintain platform security',
              'Detect and prevent misuse or unauthorised activity',
              'Improve AutoPostr and its features',
              'Provide customer support',
              'Comply with applicable legal obligations',
            ]} />

            <H2>3. Meta Platforms (Facebook, Instagram and Threads)</H2>
            <P>
              AutoPostr integrates with Meta services, including Facebook, Instagram and
              Threads, through official Meta APIs. Users voluntarily authorise AutoPostr to
              access the information and functionality necessary to provide the social-media
              management and publishing features requested by the user.
            </P>
            <P>
              AutoPostr does not request or store users&rsquo; Facebook, Instagram or Threads
              passwords. Authentication is performed through the official Meta authorisation
              flows, and AutoPostr only receives the access permissions that the user
              explicitly grants during that process.
            </P>
            <P>
              AutoPostr only accesses information and functionality permitted by the
              authorisations and permissions granted by the user and made available through
              the relevant Meta APIs. If a user changes or revokes those permissions, AutoPostr
              will lose the corresponding access.
            </P>

            <H2>4. Social-Media Access Tokens</H2>
            <P>
              Authentication tokens and access credentials required to connect supported
              social-media accounts are stored securely on our infrastructure and are used
              only to provide functionality authorised by the user. Access to these tokens is
              restricted to systems and personnel that require it in order to operate the
              service.
            </P>

            <H2>5. Data Sharing and Third Parties</H2>
            <P>
              AutoPostr may share or process information with third-party service providers
              where reasonably necessary to operate and provide the service. These may
              include:
            </P>
            <UL items={[
              'Hosting and cloud infrastructure providers',
              'Database and infrastructure providers',
              'Analytics providers',
              'Payment processors',
              'Email and communications providers',
              'Social-media platforms and APIs (including Meta and other supported platforms)',
              'Security and fraud-prevention providers',
              'Other service providers necessary to operate AutoPostr',
            ]} />
            <P>
              These providers are engaged to perform specific functions on our behalf and are
              expected to handle information in a manner consistent with this Privacy Policy
              and applicable law.
            </P>
            <P>
              <strong className="text-white">AutoPostr does not sell users&rsquo; personal information.</strong>
            </P>

            <H2>6. Data Retention</H2>
            <P>
              AutoPostr retains personal information and connected-account information only
              for as long as reasonably necessary to:
            </P>
            <UL items={[
              'Provide the service',
              'Maintain user accounts',
              'Provide requested functionality',
              'Comply with legal obligations',
              'Resolve disputes',
              'Enforce agreements',
              'Maintain security and prevent abuse',
            ]} />
            <P>
              When information is no longer required, it may be deleted or securely disposed
              of, subject to applicable legal and operational requirements.
            </P>

            <H2>7. Disconnecting Social-Media Accounts</H2>
            <P>
              Users can disconnect supported Facebook, Instagram and Threads accounts from
              their AutoPostr account through the account settings, where the functionality
              is provided.
            </P>
            <P>
              Disconnecting a social-media account stops AutoPostr from using that connection
              for future authorised actions, subject to information that AutoPostr may be
              required or permitted to retain for legitimate or legal purposes (for example,
              to comply with legal obligations, prevent fraud or resolve disputes).
            </P>

            <H2>8. Data Deletion</H2>
            <P>
              Users may request deletion of their AutoPostr account and associated personal
              information at any time. For instructions and to submit a request, please visit
              our data-deletion page:
            </P>
            <p className="my-4">
              <a
                href="/data-deletion"
                data-testid="privacy-data-deletion-link"
                className="inline-block px-5 py-2.5 rounded-full bg-emerald-400 hover:bg-emerald-500 text-black font-semibold transition-colors"
              >
                autopostr.co.uk/data-deletion
              </a>
            </p>

            <H2>9. Security</H2>
            <P>
              AutoPostr uses reasonable technical and organisational measures designed to
              protect personal information against unauthorised access, loss, misuse,
              alteration or disclosure. No method of transmission or electronic storage is
              entirely risk-free, and while we work to protect user information, we cannot
              guarantee its absolute security.
            </P>

            <H2>10. Children&rsquo;s Privacy</H2>
            <P>
              AutoPostr is not intended for children where use of the service would be
              prohibited by applicable law. Users must meet any minimum age requirements set
              by their local law and by the social-media platforms they connect through
              AutoPostr. If we become aware that we have collected personal information from
              a person who does not meet these requirements, we will take reasonable steps to
              delete that information.
            </P>

            <H2>11. International Data Transfers</H2>
            <P>
              AutoPostr and its service providers may process information in countries outside
              the user&rsquo;s country of residence. Where required by applicable law,
              appropriate safeguards will be used for international transfers of personal
              information.
            </P>

            <H2>12. Your Privacy Rights</H2>
            <P>
              Depending on the user&rsquo;s location and applicable law (including the UK
              GDPR where it applies), users may have rights such as:
            </P>
            <UL items={[
              'Accessing their personal information',
              'Requesting correction of inaccurate information',
              'Requesting deletion of personal information',
              'Requesting restriction of processing',
              'Objecting to certain types of processing',
              'Requesting data portability where applicable',
              'Withdrawing consent where processing is based on consent',
            ]} />
            <P>
              To exercise any of these rights, or to ask a privacy-related question, users can
              contact AutoPostr using the details in section 13. We may need to verify identity
              before responding to a request.
            </P>

            <H2>13. Contact</H2>
            <P>
              For privacy questions, data-protection requests or any other matter relating to
              this Privacy Policy, please contact us at:
            </P>
            <p className="my-4">
              <a
                href="mailto:support@autopostr.co.uk"
                data-testid="privacy-contact-email"
                className="text-emerald-400 hover:text-emerald-300 underline"
              >
                support@autopostr.co.uk
              </a>
            </p>

            <H2>14. Updates to This Privacy Policy</H2>
            <P>
              AutoPostr may update this Privacy Policy from time to time to reflect changes to
              the service, to our practices, or for legal, regulatory or operational reasons.
              The latest version will always be published at:
            </P>
            <p className="my-4">
              <a
                href="/privacy-policy"
                className="text-emerald-400 hover:text-emerald-300 underline break-all"
              >
                https://autopostr.co.uk/privacy-policy
              </a>
            </p>
            <P>
              We encourage users to review this page periodically to stay informed about how
              AutoPostr handles personal information.
            </P>

            <hr className="border-white/10 my-10" />
            <p className="text-white/40 text-sm">
              Effective date: {EFFECTIVE_DATE}
            </p>
          </section>
        </article>
      </main>
      <Footer />
    </div>
  );
};

// ==== Small helpers - keep styles centralised ====
const H2 = ({ children }) => (
  <h2 className="text-xl md:text-2xl font-bold text-white mt-10 mb-4">{children}</h2>
);
const P = ({ children }) => (
  <p className="text-white/70 leading-relaxed mb-4">{children}</p>
);
const UL = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1.5 mb-4 text-white/70">
    {items.map((t, i) => <li key={i}>{t}</li>)}
  </ul>
);

export default PrivacyPolicyPage;
